import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { Beam, Lean } from "../ds/Beam"
import "../ds/ds.css"
import { PRIVACY_URL } from "../privacy"
import { COVERAGE_STATS, COVERAGE_TOTAL } from "./coverage"
import { RevealText } from "./RevealText"
import { CONTACT, SiteLayout, StartButtons } from "./SiteLayout"
import { SiteGlimpse } from "./SiteGlimpse"

/** In the order your week happens (handoff 5 §3): the tier once, what it does, a small worked example. */
const WEEK: { tier: string; paid: boolean; name: string; body: string; example: ReactNode }[] = [
  {
    tier: "Free", paid: false, name: "Research",
    body: "The law, the decisions for and against, weighed for your State. Share, forward, print.",
    example: (
      <>
        <div className="kk">Example · interest on credit never used</div>
        <div className="r"><span><Lean lean="helps" label={null} title="Helps" className="nst-lean" /> 3 help, 1 against</span><span>Leans for you</span></div>
        <div className="r"><span>Interest asked</span><span className="m">₹3,10,000</span></div>
      </>
    ),
  },
  {
    tier: "Pro · first one free", paid: true, name: "Notice analysis",
    body: "The time limit year by year, what it could cost, and the officer's likely call on each allegation.",
    example: (
      <>
        <div className="kk">Example · ASMT-10 for FY 2021-22</div>
        <div className="r"><span>Reply within</span><span className="m">30 days</span></div>
        <div className="r"><span>Difference flagged</span><span className="m">₹6,40,000</span></div>
      </>
    ),
  },
  {
    tier: "Pro · first one free", paid: true, name: "Appeal or not",
    body: "Appeal-by, pre-deposit, forum, and the points the order's own text leaves open. Never a predicted result.",
    example: (
      <>
        <div className="kk">Example · order confirming ₹3,40,000</div>
        <div className="r"><span>Pre-deposit, 10%</span><span className="m">₹34,000</span></div>
        <div className="r"><span>Points to raise</span><span className="m">3</span></div>
      </>
    ),
  },
  {
    tier: "For firms", paid: false, name: "Every client, one desk",
    body: "Every notice, order and deadline in one place, in workspaces. Connected to the books, the exposure before the notice comes.",
    example: (
      <>
        <div className="kk">Next · exposure, before the notice</div>
        <div className="r"><span>Carrying, by section</span><span className="m">s.16(2)(c) · ₹8.2L</span></div>
        <div className="r"><span>Fix by</span><span className="m">GSTR-3B · Nov</span></div>
      </>
    ),
  },
]

const RULES: { b: string; s: string }[] = [
  { b: "Every claim links to its source.", s: "A decision opens to the paragraph." },
  { b: "Every rupee and date is computed, with the working.", s: "Never from a model." },
  { b: "A likely call is marked as a reading.", s: "Nira says what the officer is likely to decide and why. It never predicts an appeal's outcome." },
  { b: "Honest about what it cannot see.", s: "Every answer ends with what Nira could not check." },
  { b: "Your documents are read for your answer and nothing else.", s: "Never used to train a model. Deleted from Nira when you delete the workspace. Held under the DPDP Act, 2023." },
]

/** The home page's stats: the coverage page carries advance rulings (handoff 5 §5). */
const STATS = COVERAGE_STATS.filter((s) => !/advance rulings/i.test(s.l)).map((s) => ({ ...s, l: s.l.replace(/, with .*$/, "") }))

const NOTE = "mailto:contact@nitiassistai.com?subject=Nira%20investor%20note"

/** Nira's public home (Site-Home v2, handoff 5): the glimpse, the week, the proof, the thesis, who, pricing. */
export default function SiteHome() {
  return (
    <SiteLayout title="Where you stand on your next tax dispute">
      <section className="nst-hero ng-sec">
        <div className="nst-wrap">
          <SiteGlimpse
            intro={
              <div className="ng-copy">
                <Beam state="idle" size="hero" label={null} />
                <RevealText as="h1" text="Where you stand on your next tax dispute." />
                <p className="nst-lede nst-rv nst-rv3">
                  The law, the decisions for and against you, the rupees at stake, and what to do by when. The court's own words behind every point.
                </p>
              </div>
            }
          />
        </div>
      </section>

      <section className="nst-sec nst-tint" id="product">
        <div className="nst-wrap nst-stack">
          <div className="nst-head-block" data-rv="up">
            <p className="nst-k">In the order your week happens</p>
            <h2 className="nst-h2 narrow">Ask for free. When a notice comes, Nira reads it the way the officer will.</h2>
            <p className="nst-p">Free GST research. The work product, the department's side of a notice and the appeal reading, is paid. The first of each is free in full, so you can judge it on your own matter.</p>
          </div>
          <div className="nw" data-rv="stagger">
            {WEEK.map((w, i) => (
              <div key={w.name} className="nw-row">
                <div className="nw-tier"><span className={`nst-k${w.paid ? "" : " muted"}`}>{w.tier}</span><b>{w.name}</b></div>
                <p className="nw-body">{w.body}</p>
                <div className="nw-mini">{w.example}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nst-sec" id="coverage">
        <div className="nst-wrap np">
          <div className="np-col" data-rv="up">
            <p className="nst-k">What it reads</p>
            <h2 className="nst-h2">{COVERAGE_TOTAL} decisions. And an honest list of what is not in yet.</h2>
            <div className="np-stats" data-rv="stagger">
              {STATS.map((s, i) => (
                <div key={s.l} className="np-stat"><div className="n">{s.n}</div><div className="l">{s.l}</div></div>
              ))}
            </div>
            <Link className="nst-more" to="/coverage">Court by court, year by year, and what is being added →</Link>
          </div>
          <div className="np-col">
            <p className="nst-k">The rules behind every answer</p>
            <ol className="np-rules" data-rv="stagger">
              {RULES.map((r, i) => (
                <li key={r.b}><span className="n">{String(i + 1).padStart(2, "0")}</span><div><b>{r.b}</b> {r.s}</div></li>
              ))}
            </ol>
            <div className="np-links">
              <Link className="nst-more" to={PRIVACY_URL}>What Nira does with your documents →</Link>
              <Link className="nst-more" to="/how-we-work">How we work, in full →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="nst-navy nt">
        <div className="nst-wrap nt-row">
          <div className="nt-copy" data-rv="up">
            <p className="nst-k">Why it can say what the officer will say</p>
            <h2 className="nst-h2">The same engine sits on both sides of the table.</h2>
            <p>Nira runs on the engine Praxeum built for adjudicating officers, the one that drafts the order from the notice and the record. Point it at your notice and it tells you how that order is likely to read, before it is written.</p>
            <p>Today it answers when a notice arrives. Next, connected to a firm's books, it shows the exposure before the notice comes. The site is the proof.</p>
          </div>
          <div className="nt-cards" data-rv="stagger">
            <div className="nt-card">
              <b>For firms</b>
              <span>Seats, the workbench, and exposure on your clients' books.</span>
              <a className="nst-btn primary on-navy" href={`${CONTACT}%20for%20firms`}>Talk to us</a>
            </div>
            <div className="nt-card">
              <b>For investors and partners</b>
              <span>The thesis and where it goes, in one page.</span>
              <a className="nst-btn ghost-navy" href={NOTE}>Read the note</a>
            </div>
          </div>
        </div>
      </section>

      <section className="nst-sec">
        <div className="nst-wrap nst-stack">
          <p className="nst-k">Who it is for</p>
          <div className="nst-who" data-rv="stagger">
            <div><p className="nst-k muted">If you hold a notice</p><b className="nw-b">Calm and clarity in a minute.</b><p className="nst-p">Whether it is serious, what it could cost, and what to send your CA.</p></div>
            <div><p className="nst-k muted">If you draft replies</p><b className="nw-b">Citations with paragraphs.</b><p className="nst-p">Binding scope by State, text you can lift, and the officer's reading to write against.</p></div>
            <div><p className="nst-k muted">If you sign them</p><b className="nw-b">A client view you can forward.</b><p className="nst-p">Polished enough to go out under your own name.</p></div>
          </div>
        </div>
      </section>

      <section className="nst-sec nst-tint" id="pricing">
        <div className="nst-wrap nst-stack">
          <div className="npr-head" data-rv="up">
            <div><p className="nst-k">Pricing</p><h2 className="nst-h2">Research is free. The work product is paid.</h2></div>
            <Link className="nst-more" to="/pricing">Full pricing →</Link>
          </div>
          <div className="npr" data-rv="stagger">
            <div className="npr-c"><div className="nm">Free</div><div className="pr">₹0 · no credit card needed</div><p>10 research questions a day in your first month, then 5 a month. Your first notice analysis and first appeal reading, in full.</p></div>
            <div className="npr-c"><div className="nm">Nira Plus</div><div className="pr">₹199 a month</div><p>40 questions a month, and everything in Free.</p></div>
            <div className="npr-c hi"><div className="nm">Nira Pro</div><div className="pr">₹1,999 a month</div><p>Every notice after the first, with the officer's reading, re-runs with documents, the department's reading, and order drafts.</p></div>
            <div className="npr-c"><div className="nm">Nira for firms</div><div className="pr">by entities · talk to us</div><p>Everything in Pro for every client, the workbench, your data under your control, exposure.</p></div>
          </div>
        </div>
      </section>

      <section className="nst-navy">
        <div className="nst-wrap nst-cta-row">
          <div className="nst-cta-copy" data-rv="up">
            <Beam state="static" on="navy" label={null} />
            <h2 className="nst-h2">Ask your first question.</h2>
            <p>Ask it now, no account needed.</p>
          </div>
          <StartButtons dark />
        </div>
      </section>
    </SiteLayout>
  )
}
